import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_NIGHT,
  LINE_ITEM_OFFER,
  LINE_ITEM_REQUEST,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the base price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.lineItemUnitType} props.code - The code of the line item
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemBasePriceMaybe = props => {
  const { lineItems, code, booking, intl } = props;
  const isNightly = code === LINE_ITEM_NIGHT;
  const isDaily = code === LINE_ITEM_DAY;
  const isHourly = code === LINE_ITEM_HOUR;
  const isFixed = code === LINE_ITEM_FIXED;
  const isRequest = code === LINE_ITEM_REQUEST;
  const isOffer = code === LINE_ITEM_OFFER;
  const translationKey = isNightly
    ? 'OrderBreakdown.baseUnitNight'
    : isDaily
    ? 'OrderBreakdown.baseUnitDay'
    : isHourly
    ? 'OrderBreakdown.baseUnitHour'
    : isFixed
    ? 'OrderBreakdown.baseUnitFixedBooking'
    : isRequest
    ? 'OrderBreakdown.baseUnitRequest'
    : isOffer
    ? 'OrderBreakdown.baseUnitOffer'
    : 'OrderBreakdown.baseUnitQuantity';

  // Find correct line-item for given code prop.
  // It should be one of the following: 'line-item/night, 'line-item/day', 'line-item/hour', 'line-item/fixed', 'line-item/item', 'line-item/offer', 'line-item/request'
  // These are defined in '../../util/types';
  const unitPurchase = lineItems.find(item => item.code === code && !item.reversal);

  const quantity = unitPurchase?.units
    ? unitPurchase.units.toString()
    : unitPurchase?.quantity
    ? unitPurchase.quantity.toString()
    : null;
  const unitPrice = unitPurchase ? formatMoney(intl, unitPurchase.unitPrice) : null;
  const total = unitPurchase ? formatMoney(intl, unitPurchase.lineTotal) : null;

  // For flat-rate hourly slot bookings, the server emits quantity=1 even when the
  // actual booking duration spans multiple hours (or a fractional hour). Compute
  // the true duration from the booking timestamps so the label reflects reality.
  const actualHours = (() => {
    const start = booking?.attributes?.start;
    const end = booking?.attributes?.end;
    if (!start || !end) return null;
    const diffMs = end.valueOf() - start.valueOf();
    if (!Number.isFinite(diffMs) || diffMs <= 0) return null;
    // Round to 1 decimal place to handle float drift (e.g., 2.4999 → 2.5),
    // but preserve half-hour precision (2.5 stays 2.5, not 3).
    return Math.round((diffMs / 3600000) * 10) / 10;
  })();

  const shouldOverride =
    code === LINE_ITEM_HOUR && actualHours !== null && Number(quantity) === 1 && actualHours !== 1;

  let message;
  if (shouldOverride) {
    // Flat-rate slot: omit unitPrice from the left column — price shows only in
    // the right-hand Total column (lineTotal, rendered below, untouched).
    const useMinutes = actualHours < 1;
    const flatKeyBase = useMinutes
      ? 'OrderBreakdown.baseUnitMinutesFlat'
      : 'OrderBreakdown.baseUnitHourFlat';
    const flatQuantity = useMinutes ? Math.round(actualHours * 60) : actualHours;
    message = unitPurchase?.seats ? (
      <FormattedMessage
        id={`${flatKeyBase}Seats`}
        values={{ quantity: flatQuantity, seats: unitPurchase.seats }}
      />
    ) : (
      <FormattedMessage id={flatKeyBase} values={{ quantity: flatQuantity }} />
    );
  } else {
    message = unitPurchase?.seats ? (
      <FormattedMessage
        id={`${translationKey}Seats`}
        values={{ unitPrice, quantity, seats: unitPurchase.seats }}
      />
    ) : (
      <FormattedMessage id={translationKey} values={{ unitPrice, quantity }} />
    );
  }

  return quantity && total ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>{message}</span>
      <span className={css.itemValue}>{total}</span>
    </div>
  ) : null;
};

export default LineItemBasePriceMaybe;
